import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { GET_PENDING_ORDERS } from '../graphql/orders';
import { GET_LOST_FOUND_ITEMS } from '../graphql/lost-found-queries';
import { GET_WORK_ORDERS } from '../graphql/queries';
import { LOST_FOUND_CATEGORY } from './LostFoundForm';
import { useAuth } from '../contexts/AuthContext';
import {
  Badge,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
  Divider,
  Chip,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  ShoppingCart as OrderIcon,
  FindInPage as LostFoundIcon,
  Build as WorkOrderIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

const STORAGE_KEYS = {
  orders: (userId) => `notificationSeen_orders_${userId}`,
  lostFound: (userId) => `notificationSeen_lostFound_${userId}`,
  workOrders: (userId) => `notificationSeen_workOrders_${userId}`,
};

function loadSeenFromStorage(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveSeenToStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn('Could not save notification seen state', e);
  }
}

/**
 * Updates seen state and immediately persists to localStorage.
 * Returns the new merged map so callers can use it for setState.
 */
function markSeenAndPersist(storageKey, prevSeen, ids) {
  const next = { ...prevSeen };
  let changed = false;
  ids.forEach((id) => {
    if (!next[id]) { next[id] = true; changed = true; }
  });
  if (changed) saveSeenToStorage(storageKey, next);
  return changed ? next : prevSeen;
}

const STATUS_COLORS = {
  OPEN: { bg: '#fef3c7', color: '#92400e' },
  IN_PROGRESS: { bg: '#dbeafe', color: '#1e40af' },
};

function OrderNotifications() {
  const navigate = useNavigate();
  const { user, isLoggingOut } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [seenOrders, setSeenOrders] = useState({});
  const [seenLostFound, setSeenLostFound] = useState({});
  const [seenWorkOrders, setSeenWorkOrders] = useState({});

  const userId = user?.id != null ? String(user.id) : null;
  const isAdmin = user?.role === 'ADMIN';

  // Keep a ref to userId so storage helpers always have the latest value
  const userIdRef = useRef(userId);
  useEffect(() => { userIdRef.current = userId; }, [userId]);

  // Load per-user seen state from localStorage (read-only, no save effects)
  useEffect(() => {
    if (!userId) {
      setSeenOrders({});
      setSeenLostFound({});
      setSeenWorkOrders({});
      return;
    }
    setSeenOrders(loadSeenFromStorage(STORAGE_KEYS.orders(userId)));
    setSeenLostFound(loadSeenFromStorage(STORAGE_KEYS.lostFound(userId)));
    setSeenWorkOrders(loadSeenFromStorage(STORAGE_KEYS.workOrders(userId)));
  }, [userId]);

  // ── Pending orders (admin only) ──
  const { data: ordersData } = useQuery(GET_PENDING_ORDERS, {
    pollInterval: 130000,
    fetchPolicy: 'network-only',
    skip: !isAdmin || isLoggingOut,
  });

  // ── Reported lost items (all roles) ──
  const canPollLostFound = user?.role !== 'INSPECTOR';
  const { data: lostFoundData } = useQuery(GET_LOST_FOUND_ITEMS, {
    variables: {
      page: 1,
      limit: 20,
      isArchived: false,
      category: LOST_FOUND_CATEGORY.REPORTED_LOST_ITEM,
    },
    pollInterval: 60000,
    fetchPolicy: 'network-only',
    skip: !user || isLoggingOut || !canPollLostFound,
  });

  // ── Work orders assigned to current user, not completed ──
  const { data: workOrdersData } = useQuery(GET_WORK_ORDERS, {
    variables: {
      input: {
        assignedToMe: true,
        page: 1,
        limit: 20,
      },
    },
    pollInterval: 60000,
    fetchPolicy: 'network-only',
    skip: !user || isLoggingOut,
  });

  const pendingOrders = ordersData?.findPendingOrders ?? [];
  const pendingLostFound = lostFoundData?.lostFoundItems?.items ?? [];
  const myWorkOrders = (workOrdersData?.workOrders?.items ?? []).filter(
    (wo) => wo.status !== 'COMPLETED' && wo.status !== 'CANCELED',
  );

  // Badge count: sum of unseen items across all sections
  useEffect(() => {
    const unseenOrders = isAdmin ? pendingOrders.filter((o) => !seenOrders[o.id]).length : 0;
    const unseenLostFound = pendingLostFound.filter((i) => !seenLostFound[i.id]).length;
    const unseenWorkOrders = myWorkOrders.filter((wo) => !seenWorkOrders[wo.id]).length;
    setNotificationCount(unseenOrders + unseenLostFound + unseenWorkOrders);
  }, [isAdmin, pendingOrders, pendingLostFound, myWorkOrders, seenOrders, seenLostFound, seenWorkOrders]);

  const handleOpenDrawer = () => setDrawerOpen(true);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    const uid = userIdRef.current;
    if (!uid) return;

    setSeenOrders((prev) => markSeenAndPersist(STORAGE_KEYS.orders(uid), prev, pendingOrders.map((o) => o.id)));
    setSeenLostFound((prev) => markSeenAndPersist(STORAGE_KEYS.lostFound(uid), prev, pendingLostFound.map((i) => i.id)));
    setSeenWorkOrders((prev) => markSeenAndPersist(STORAGE_KEYS.workOrders(uid), prev, myWorkOrders.map((wo) => wo.id)));
  }, [pendingOrders, pendingLostFound, myWorkOrders]);

  const handleOrderClick = (orderId) => {
    navigate(`/orders?id=${orderId}`);
    setDrawerOpen(false);
    const uid = userIdRef.current;
    if (!uid) return;
    setSeenOrders((prev) => markSeenAndPersist(STORAGE_KEYS.orders(uid), prev, [orderId]));
  };

  const handleLostFoundClick = (itemId) => {
    navigate(`/dashboard/lost-found/${itemId}`);
    setDrawerOpen(false);
    const uid = userIdRef.current;
    if (!uid) return;
    setSeenLostFound((prev) => markSeenAndPersist(STORAGE_KEYS.lostFound(uid), prev, [itemId]));
  };

  const handleWorkOrderClick = (workOrderId) => {
    navigate(`/dashboard/work-orders/${workOrderId}`);
    setDrawerOpen(false);
    const uid = userIdRef.current;
    if (!uid) return;
    setSeenWorkOrders((prev) => markSeenAndPersist(STORAGE_KEYS.workOrders(uid), prev, [workOrderId]));
  };

  const hasOrders = isAdmin && pendingOrders.length > 0;
  const hasLostFound = pendingLostFound.length > 0;
  const hasWorkOrders = myWorkOrders.length > 0;

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleOpenDrawer}
          sx={{
            color: '#082f49',
            '&:hover': { backgroundColor: 'transparent' },
          }}
          aria-label={notificationCount > 0 ? `${notificationCount} new notifications` : 'Notifications'}
        >
          <Badge badgeContent={notificationCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Drawer anchor="right" open={drawerOpen} onClose={handleCloseDrawer}>
        <Box sx={{ width: 360, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Notifications</Typography>
            <IconButton onClick={handleCloseDrawer} aria-label="Close notifications">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* ── New orders (admin only) ── */}
          {isAdmin && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <OrderIcon fontSize="small" /> New orders
              </Typography>
              {!hasOrders ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No pending orders
                </Typography>
              ) : (
                <List dense disablePadding sx={{ mb: 2 }}>
                  {pendingOrders.map((order) => (
                    <ListItem key={order.id} disablePadding>
                      <ListItemButton onClick={() => handleOrderClick(order.id)} sx={{ borderRadius: 1 }}>
                        <ListItemText
                          primary={`Order #${order.id.substring(0, 8)}`}
                          secondary={
                            <>
                              <Typography variant="body2" component="span">
                                ${order.total?.toFixed(2) ?? '0.00'} – {format(new Date(order.createdAt), 'MMM d, h:mm a')}
                              </Typography>
                              <br />
                              <Typography variant="caption" component="span">
                                {order.billingName || 'Customer'}
                              </Typography>
                            </>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}

              {(hasLostFound || hasWorkOrders) && <Divider sx={{ my: 2 }} />}
            </>
          )}

          {/* ── Reported lost items (all roles) ── */}
          <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <LostFoundIcon fontSize="small" /> Reported lost items
          </Typography>
          {!hasLostFound ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No new reported lost items
            </Typography>
          ) : (
            <List dense disablePadding sx={{ mb: 2 }}>
              {pendingLostFound.map((item) => {
                const desc = item.description?.length > 40 ? `${item.description.slice(0, 40)}…` : item.description;
                return (
                  <ListItem key={item.id} disablePadding>
                    <ListItemButton onClick={() => handleLostFoundClick(item.id)} sx={{ borderRadius: 1 }}>
                      <ListItemText
                        primary={item.property?.name ?? 'Unknown property'}
                        secondary={
                          <>
                            <Typography variant="body2" component="span">
                              {desc || 'No description'}
                            </Typography>
                            <br />
                            <Typography variant="caption" component="span">
                              {format(new Date(item.createdAt), 'MMM d, h:mm a')}
                              {item.submitter ? ` · ${item.submitter.firstName} ${item.submitter.lastName}` : ''}
                            </Typography>
                          </>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}

          {/* ── Work orders assigned to me (all roles) ── */}
          {(hasLostFound || (isAdmin && hasOrders)) && <Divider sx={{ my: 2 }} />}

          <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <WorkOrderIcon fontSize="small" /> My work orders
          </Typography>
          {!hasWorkOrders ? (
            <Typography variant="body2" color="text.secondary">
              No active work orders assigned to you
            </Typography>
          ) : (
            <List dense disablePadding>
              {myWorkOrders.map((wo) => {
                const desc = wo.description?.length > 50 ? `${wo.description.slice(0, 50)}…` : wo.description;
                const statusStyle = STATUS_COLORS[wo.status] || { bg: '#f3f4f6', color: '#374151' };
                const statusLabel = wo.status === 'IN_PROGRESS' ? 'In progress' : wo.status === 'OPEN' ? 'Open' : wo.status;
                return (
                  <ListItem key={wo.id} disablePadding>
                    <ListItemButton onClick={() => handleWorkOrderClick(wo.id)} sx={{ borderRadius: 1 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {wo.property?.name ?? 'Unknown property'}
                            </Typography>
                            <Chip
                              label={statusLabel}
                              size="small"
                              sx={{ height: 20, fontSize: '0.7rem', backgroundColor: statusStyle.bg, color: statusStyle.color }}
                            />
                            {wo.isUrgent && (
                              <Chip label="Urgent" size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" component="span">
                              {desc || 'No description'}
                            </Typography>
                            <br />
                            <Typography variant="caption" component="span">
                              {format(new Date(wo.createdAt), 'MMM d, h:mm a')}
                              {wo.submittedBy ? ` · ${wo.submittedBy.name}` : ''}
                            </Typography>
                          </>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      </Drawer>
    </>
  );
}

export default OrderNotifications;
